/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = this.querySelectorAll('.slider__item');

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));
      }

      init() {
        super.init();

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                // Set the active set of slideshow images
                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          // Show the current slider item
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slider.slides[1]?.setAttribute('aria-current', 'true');
          }
        }
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);
        });

        this.setButtonStates();
      }

      /**
       * Handles 'change' events in the product card swatches.
       * @param {object} evt - Event object.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const left = variantMedia.closest('.slider__item').offsetLeft;
            this.slider.scrollTo({ left, behavior: 'instant' });
          }
        }
      }

      /**
       * Sets the disabled state of the nav buttons.
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
          return;
        }

        const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan) + 1;
        const visibleSlideCount = Array.from(this.slides).filter((slide) => slide.hidden !== true).length;

        this.prevBtn.disabled = currentSlideIndex === 1
          || (this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0);
        this.nextBtn.disabled = visibleSlideCount === currentSlideIndex
          || this.getSlideVisibility(this.slides[this.slides.length - 1]);
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      scrollInProgress() {
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      handleScroll() {
        super.handleScroll();
        this.slides[this.currentIndex].setAttribute('aria-current', 'true');
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}

/* HHT-MOBILE-CARD-SLIDER-START */
(() => {
  const mobile = () =>
    window.matchMedia('(max-width: 767px)').matches;

  const visibleSlides = (slider) =>
    Array.from(slider.querySelectorAll('.slider__item'))
      .filter((slide) => !slide.hidden);

  const enableButtons = (root = document) => {
    if (!mobile()) return;

    root.querySelectorAll('product-card-image-slider')
      .forEach((component) => {
        const slider = component.querySelector('.slider');
        if (!slider || visibleSlides(slider).length < 2) return;

        component.querySelectorAll('.slider-nav__btn')
          .forEach((button) => {
            button.disabled = false;
            button.removeAttribute('disabled');
            button.style.pointerEvents = 'auto';
            button.style.touchAction = 'manipulation';
          });
      });
  };

  document.addEventListener('click', (event) => {
    if (!mobile()) return;

    const button = event.target.closest(
      'product-card-image-slider .slider-nav__btn'
    );

    if (!button) return;

    const sliderId = button.getAttribute('aria-controls');
    const slider = sliderId
      ? document.getElementById(sliderId)
      : null;

    if (!slider) return;

    const slides = visibleSlides(slider);
    if (slides.length < 2) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    let index = slides.findIndex((slide) =>
      slide.hasAttribute('aria-current')
    );

    if (index < 0) {
      index = slides.reduce((closest, slide, current) => {
        const distance = Math.abs(
          slide.offsetLeft - slider.scrollLeft
        );
        const closestDistance = Math.abs(
          slides[closest].offsetLeft - slider.scrollLeft
        );

        return distance < closestDistance ? current : closest;
      }, 0);
    }

    const direction = button.name === 'prev' ? -1 : 1;
    const next = (index + direction + slides.length) % slides.length;

    slides.forEach((slide) =>
      slide.removeAttribute('aria-current')
    );

    slides[next].setAttribute('aria-current', 'true');

    slider.scrollTo({
      left: slides[next].offsetLeft,
      behavior: 'smooth'
    });

    window.setTimeout(enableButtons, 350);
  }, true);

  const start = () => {
    enableButtons();

    new MutationObserver(() => enableButtons())
      .observe(document.body, {
        childList: true,
        subtree: true
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('resize', enableButtons);
})();
/* HHT-MOBILE-CARD-SLIDER-END */

/* HHT-TOUCH-SWIPE-START */
(() => {
  if (window.HHTProductCardTouchSwipeInstalled) return;
  window.HHTProductCardTouchSwipeInstalled = true;

  const starts = new WeakMap();
  const swiped = new WeakSet();

  document.addEventListener('touchstart', (event) => {
    const component = event.target.closest('product-card-image-slider');
    if (!component || !event.touches.length) return;

    starts.set(component, {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY
    });
  }, { passive: true });

  document.addEventListener('touchend', (event) => {
    const component = event.target.closest('product-card-image-slider');
    if (!component || !event.changedTouches.length) return;

    const start = starts.get(component);
    if (!start) return;

    const endX = event.changedTouches[0].clientX;
    const endY = event.changedTouches[0].clientY;
    const deltaX = endX - start.x;
    const deltaY = endY - start.y;

    starts.delete(component);

    if (Math.abs(deltaX) < 35) return;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

    const slider = component.querySelector('.slider');
    if (!slider) return;

    const slides = Array.from(
      component.querySelectorAll('.slider__item:not([hidden])')
    );

    if (slides.length < 2) return;

    let currentIndex = slides.findIndex(
      slide => slide.hasAttribute('aria-current')
    );

    if (currentIndex < 0) {
      currentIndex = slides.reduce((closest, slide, index) => {
        const currentDistance = Math.abs(
          slide.offsetLeft - slider.scrollLeft
        );
        const closestDistance = Math.abs(
          slides[closest].offsetLeft - slider.scrollLeft
        );

        return currentDistance < closestDistance ? index : closest;
      }, 0);
    }

    let targetIndex;

    if (deltaX < 0) {
      targetIndex = Math.min(currentIndex + 1, slides.length - 1);
    } else {
      targetIndex = Math.max(currentIndex - 1, 0);
    }

    if (targetIndex === currentIndex) return;

    slides.forEach(slide => slide.removeAttribute('aria-current'));
    slides[targetIndex].setAttribute('aria-current', 'true');

    slider.scrollTo({
      left: slides[targetIndex].offsetLeft,
      behavior: 'smooth'
    });

    swiped.add(component);

    window.setTimeout(() => {
      swiped.delete(component);
    }, 500);
  }, { passive: true });

  document.addEventListener('click', (event) => {
    const component = event.target.closest('product-card-image-slider');

    if (component && swiped.has(component)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
})();
/* HHT-TOUCH-SWIPE-END */
